import { deleteOrganizationUsersApi, useOrganizationUserList } from "@/api/services/admin/organization";
import { TOrganizationUserDto } from "@/api/services/admin/organization/dtos/get-organization-detail.dto";
import { useEnum } from "@/api/services/global/enum";
import { IconTrash } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { Modal } from "@/components/elements/Modal";
import { SimplePagination } from "@/components/elements/SimplePagination";
import { Table } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_FORMAT } from "@/constants/datetime";
import { EUserStatus } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { getPICGuardOffenders, TCannotDeactivateUser, UserCannotDeactivateModal } from "@/components/commons/UserCannotDeactivateModal";
import { ContractServiceBadge } from "@/features/organization/components/ContractServiceBadge";
import { useFillViewportHeight } from "@/hooks/useFillViewportHeight";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { useToast } from "@/hooks/useToast";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { state } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { isApiError, showError } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { MenuItem, MenuList, Popover, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

type OrganizationUserTableProps = {
  onTotalChange?: (total: number) => void;
  readOnly?: boolean;
};

export const OrganizationUserTable: FC<OrganizationUserTableProps> = ({ onTotalChange, readOnly }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { ref: tableAreaRef, maxHeight: tableMaxHeight } = useFillViewportHeight<HTMLDivElement>({ bottomGap: 176 });

  const [page, setPage] = useState(1);
  const [deleteMode, setDeleteMode] = useState(false);

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const { visible: menuOpen, open: openMenu, close: closeMenu } = useVisible(false);
  const { visible: confirmOpen, open: openConfirm, close: closeConfirm } = useVisible(false);
  const { visible: CannotDeactivateOpen, open: openCannotDeactivate, close: closeCannotDeactivate } = useVisible(false);
  const [usersCannotDeactivate, setUsersCannotDeactivate] = useState<TCannotDeactivateUser[]>([]);
  const [selectedCountCannotDeactivate, setSelectedCountCannotDeactivate] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const handleEnterDeleteMode = () => {
    closeMenu();
    clearSelection();
    setDeleteMode(true);
  };

  const handleCancelDeleteMode = () => {
    setDeleteMode(false);
    clearSelection();
  };

  const handleDeleteUsers = async () => {
    setErrorMessage("");
    try {
      await deleteOrganizationUsersApi(rowSelectionKeys.map(Number));
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_LIST] });
      closeConfirm();
      handleCancelDeleteMode();
    } catch (error) {
      if (isApiError(error)) {
        setErrorMessage(error.message);
      }
      closeConfirm();
      // Blocked because a selected user is an org's 担当者 — list the offenders in a modal.
      const offenders = getPICGuardOffenders(error);
      if (offenders) {
        setUsersCannotDeactivate(offenders);
        setSelectedCountCannotDeactivate(rowSelectionKeys.length);
        openCannotDeactivate();
        return;
      }
      showError(error);
    }
  };

  const { data, isLoading, isFetching } = useOrganizationUserList({ page });
  const { data: userRoles } = useEnum("EUserRole");
  const { data: userStatuses } = useEnum("EUserStatus");

  const items = data?.data ?? [];
  const pagination = data?.pagination;

  useEffect(() => {
    if (pagination?.total != null) onTotalChange?.(pagination.total);
  }, [pagination?.total, onTotalChange]);

  const columns: MRT_ColumnDef<TOrganizationUserDto>[] = useMemo(
    () => [
      {
        accessorKey: "fullName",
        header: "ユーザー名",
        Cell: ({ row }) => <EllipsisText title={row.original.fullName ?? EMPTY_STR.TEXT} variant="body14" maxWidth={168} />,
        size: 180,
      },
      {
        accessorKey: "email",
        header: "メールアドレス",
        Cell: ({ row }) => <EllipsisText title={row.original?.email ?? EMPTY_STR.TEXT} variant="body14" maxWidth={200} />,
        size: 220,
      },
      {
        accessorKey: "role",
        header: "権限",
        Cell: ({ row }) => (row.original?.role ? findLabelByValue(userRoles, row.original.role) : EMPTY_STR.TEXT),
        size: 74,
      },
      {
        id: "contractServices",
        header: "契約サービス",
        size: 120,
        Cell: ({ row }) => {
          const products = (row.original?.productContracts ?? []).map((contract) => ({
            code: contract.productCode,
            singleCharLabel: contract.productSingleCharLabel,
            name: contract.productName,
          }));
          return products.length > 0 ? <ContractServiceBadge products={products} /> : EMPTY_STR.TEXT;
        },
      },
      {
        accessorKey: "lastLoginAt",
        header: "最終ログイン",
        Cell: ({ row }) => (row.original?.lastLoginAt ? dayjs(row.original.lastLoginAt).format(DATE_FORMAT) : EMPTY_STR.DATE),
        size: 116,
      },
      {
        accessorKey: "createdAt",
        header: "登録日",
        Cell: ({ row }) => (row.original?.createdAt ? dayjs(row.original.createdAt).format(DATE_FORMAT) : EMPTY_STR.DATE),
        size: 106,
      },
      {
        id: "actions",
        header: "",
        size: 68,
        Cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(getDynamicRoute(AppRoutes.organizationUserEdit, { id: id!, userId: row.original?.id }))}
          >
            編集
          </Button>
        ),
      },
    ],
    [userRoles, userStatuses, navigate, id],
  );

  const { table, rowSelectionKeys, clearSelection } = useSelectionTable({
    primaryKey: "id",
    columns,
    rows: items,
    layoutMode: "grid",
    enableRowSelection: deleteMode,
    enableSelectAll: false,
    localization: { select: "" },
    displayColumnDefOptions: {
      "mrt-row-select": { size: 40 },
    },
    muiTableBodyRowProps: ({ row }) => {
      const isDisabled = row.original?.status === EUserStatus.DISABLED;
      return {
        sx: isDisabled
          ? {
              "& .MuiTableCell-root": {
                color: "rgba(0, 0, 0, 0.3) !important",
                "& > *:not(button)": {
                  opacity: "0.3 !important",
                },
              },
            }
          : undefined,
      };
    },
  });

  return (
    <Stack ref={tableAreaRef} gap={2}>
      <Table table={table} isLoading={isLoading} isFetching={isFetching} maxHeight={tableMaxHeight} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SimplePagination page={page} totalPage={pagination?.totalPage} onChange={setPage} />
        {deleteMode ? (
          <Stack direction="row" gap={1.5}>
            <Button size="sm" variant="ghost" onClick={handleCancelDeleteMode}>
              キャンセル
            </Button>
            <Button
              size="sm"
              variant="danger"
              startIcon={<IconTrash fontSize="14px" />}
              onClick={openConfirm}
              disabled={rowSelectionKeys.length === 0 || readOnly}
            >
              削除
            </Button>
          </Stack>
        ) : (
          <>
            <div ref={menuAnchorRef}>
              <Button
                size="sm"
                variant="outline"
                startIcon={<MoreHorizIcon />}
                endIcon={menuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                onClick={openMenu}
                disabled={readOnly}
              >
                代行操作
              </Button>
            </div>
            <Popover
              open={menuOpen}
              anchorEl={menuAnchorRef.current}
              onClose={closeMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{
                ".MuiPaper-root": {
                  px: "0px !important",
                },
              }}
            >
              <MenuList sx={{ py: 0 }}>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    navigate(AppRoutes.organizationUserCreate.replace(":id", id!));
                  }}
                  sx={{ p: 1 }}
                >
                  <Typography variant="body14">新しい組織ユーザーの登録</Typography>
                </MenuItem>
                <MenuItem onClick={handleEnterDeleteMode} sx={{ p: 1 }} disabled={readOnly}>
                  <Typography variant="body14" color={state.error_3}>
                    選択してユーザー削除
                  </Typography>
                </MenuItem>
              </MenuList>
            </Popover>
          </>
        )}
      </Stack>
      <Modal
        enableCloseIcon={false}
        isOpen={confirmOpen}
        onClose={closeConfirm}
        onOk={handleDeleteUsers}
        labelOk="削除する"
        labelClose="キャンセル"
        title="ユーザーの削除"
      >
        <Stack sx={{ m: "8px 0 24px 0" }}>
          <Typography variant="cap12">
            {`選択した${rowSelectionKeys.length}件のユーザーを削除してもよろしいですか？この操作は取り消せません。`}
          </Typography>
        </Stack>
      </Modal>

      <UserCannotDeactivateModal
        isOpen={CannotDeactivateOpen}
        onClose={closeCannotDeactivate}
        users={usersCannotDeactivate}
        selectedCount={selectedCountCannotDeactivate}
        errorMessage={errorMessage}
      />
    </Stack>
  );
};
